import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage } from "../../../locales/translationUtils";
import { useWarehouseUserClientReportDaily } from "../../../apis/warehouseUserClientReportDaily/WarehouseUserClientReportDailyProvider";
import { WarehouseUserClientReportDailyApiResponse, WarehouseUserClientReportDailyPutVM } from "../../../services/warehouseUserClientReportDailyService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import { useGeneric } from '../../../apis/generic/GenericProvider';
import { useUser } from "../../../apis/user/UserProvider";
import User from "../../../types/user";
import { FILTRO_TODOS_OS_USUARIOS } from "../../../services/userService";

type Option = {
    id: number;
    name: string;
};

const ViewWarehouseUserClientReportDailyById: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { fetchWarehouseUserClientReportDailyById, putWarehouseUserClientReportDaily, excluiWarehouseUserClientReportDaily } = useWarehouseUserClientReportDaily();
    const [warehouseUserClientReportDaily, setWarehouseUserClientReportDaily] = useState<WarehouseUserClientReportDailyApiResponse | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { distinctOptions, fetchDistinctOptions } = useGeneric();
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const { fetchUsuarios } = useUser();
    const [usuarios, setUsuarios] = useState<User[]>([]);
    const [clientsList, setClientsList] = useState<Option[]>([]);
    const [warehousesList, setWarehouseList] = useState<Option[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);

    const descriptionRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const usuariosCarregados = await fetchUsuarios(FILTRO_TODOS_OS_USUARIOS);
            setUsuarios(usuariosCarregados);
        };

        fetchData();
    }, []);

    const navItems: NavBarItem[] = [
        { label: t('warehouseUserClientReportDaily.labels.home'), route: '/', iconName: 'house-door' },
        { label: t('warehouseUserClientReportDaily.labels.list'), route: '/warehouseuserclientreportdaily', iconName: 'table' },
        { label: t('warehouseUserClientReportDaily.labels.new'), route: '/warehouseuserclientreportdaily/create', iconName: 'plus-circle' },
    ];

    useEffect(() => {
        if (isEditing && descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const loadWarehouseUserClientReportDaily = async () => {
            try {
                setLoading(true);
                setError(false);
                if (!id) throw new Error("ID inválido");

                const result = await fetchWarehouseUserClientReportDailyById(Number(id));

                setWarehouseUserClientReportDaily(result);

            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        loadWarehouseUserClientReportDaily();
    }, [id, fetchWarehouseUserClientReportDailyById]);


    useEffect(() => {
        if (isEditing) {
            (async () => {
                fetchDistinctOptions("wareHouse");
                fetchDistinctOptions("client");

                setWarehouseList(
                    (distinctOptions["wareHouse"] || []).map(item => ({
                        id: item.id,
                        name: item.description || ""
                    }))
                );

                setClientsList(
                    (distinctOptions["client"] || []).map(item => ({
                        id: item.id,
                        name: item.description || ""
                    }))
                );
            })();
        }
    }, [isEditing]);

    useEffect(() => {
        if (warehouseUserClientReportDaily?.data?.[0]) {
            const clientId = warehouseUserClientReportDaily.data[0].clientId;
            const wrId = warehouseUserClientReportDaily.data[0].wrId;
            const userId = warehouseUserClientReportDaily.data[0].userId;

            setSelectedClientId(clientId);
            setSelectedWarehouseId(wrId);
            setSelectedUserId(userId);
        }
    }, [warehouseUserClientReportDaily]);


    const preparePayload = (): WarehouseUserClientReportDailyPutVM | null => {
        if (!warehouseUserClientReportDaily) return null;

        return {
            id: warehouseUserClientReportDaily.data[0].id,
            clientId: selectedClientId,
            wrId: selectedWarehouseId,
            userId: selectedUserId,
        };
    };

    const warehouseUserClientReportDailyRef = useRef(warehouseUserClientReportDaily);

    const handleEdit = async () => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        try {
            if (!warehouseUserClientReportDaily) return;

            const payload = preparePayload();
            if (!payload) return;

            await putWarehouseUserClientReportDaily(warehouseUserClientReportDaily.data[0].id, payload);

            setWarehouseUserClientReportDaily(prev =>
                prev
                    ? {
                        ...prev,
                        data: {
                            ...prev.data,
                            client: clientsList.find(client => client.id === payload.clientId),
                            wr: warehousesList.find(warehouse => warehouse.id === payload.wrId),
                            user: warehousesList.find(user => user.id === payload.userId),
                        }
                    }
                    : prev
            );

            warehouseUserClientReportDailyRef.current = warehouseUserClientReportDaily;

            const updatedData = await fetchWarehouseUserClientReportDailyById(warehouseUserClientReportDaily.data[0].id);
            if (updatedData) {
                setWarehouseUserClientReportDaily(updatedData);
            }

            const successMessage = getSuccessMessage("editWarehouseUserClientReportDaily", { id: warehouseUserClientReportDaily.data[0].id, });
            navigate(`/WarehouseUserClientReportDaily`, {
                state: {
                    message: successMessage,
                    variant: "success",
                },
            });
        } catch (error) {
            console.error("Erro ao atualizar os dados:", error);
            alert("Erro ao atualizar os dados.");
        } finally {
            setIsEditing(false);
        }
    };



    const handleDelete = async () => {
        if (!id) return;
        try {
            await excluiWarehouseUserClientReportDaily(Number(id));

            const successMessage = getSuccessMessage("deleteWarehouseUserClientReportDaily", { id: id, });
            navigate(`/WarehouseUserClientReportDaily`, {
                state: {
                    message: successMessage,
                    variant: "success",
                },
            });
        } catch (error) {
            alert("Erro ao excluir o registro.");
        }
    };

    const handleNavigation = (route: string) => {
        navigate(route);
    };

    if (loading) return <div className="text-center mt-4">Carregando...</div>;
    if (error || !warehouseUserClientReportDaily?.data?.[0]) return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;

    return (
        <Container fluid>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editWarehouseUserClientReportDaily") : getPageName("viewWarehouseUserClientReportDaily")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Form.Group className="mb-2"></Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseUserClientReportDaily.fields.id')}</Form.Label>
                    <Form.Control type="text" value={warehouseUserClientReportDaily?.data[0]?.id || ""} readOnly style={{ width: "20%", backgroundColor: "#f0f0f0" }} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseUserClientReportDaily.fields.client')}</Form.Label>
                    {isEditing ? (
                        <Form.Select
                            value={selectedClientId ?? ""}
                            onChange={(e) => setSelectedClientId(Number(e.target.value))}
                            disabled={!isEditing}
                            style={{ width: "20%" }}
                        >
                            <option value="">{t('warehouseUserClientReportDaily.labels.selectClient')}</option>
                            {distinctOptions['client']?.map(
                                (option: { id: number; description: string }) => (
                                    <option key={option.id} value={option.id.toString()}>
                                        {option.description}
                                    </option>
                                )
                            )}
                        </Form.Select>
                    ) : (
                        <Form.Control
                            type="text"
                            value={warehouseUserClientReportDaily.data[0].clientName}
                            readOnly
                            style={{ cursor: "pointer", textDecoration: "underline", width: "20%", backgroundColor: "#f0f0f0" }}
                            onClick={() => handleNavigation(`/client/${warehouseUserClientReportDaily.data[0].clientId}`)}
                        />
                    )}
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseUserClientReportDaily.fields.from')}</Form.Label>
                    {isEditing ? (
                        <Form.Select
                            value={selectedWarehouseId ?? ""}
                            onChange={(e) => setSelectedWarehouseId(Number(e.target.value))}
                            disabled={!isEditing}
                            style={{ width: "20%" }}
                        >
                            <option value="">{t('warehouseUserClientReportDaily.labels.selectWarehouse')}</option>
                            {distinctOptions['wareHouse']?.map(
                                (option: { id: number; description: string }) => (
                                    <option key={option.id} value={option.id.toString()}>
                                        {option.description}
                                    </option>
                                )
                            )}
                        </Form.Select>
                    ) : (
                        <Form.Control
                            type="text"
                            value={warehouseUserClientReportDaily.data[0].wrName}
                            readOnly
                            style={{ cursor: "pointer", textDecoration: "underline", width: "20%", backgroundColor: "#f0f0f0" }}
                            onClick={() => handleNavigation(`/warehouse/${warehouseUserClientReportDaily.data[0].wrId}`)}
                        />
                    )}
                </Form.Group>

                <Form.Group>
                    <Form.Label>{t('warehouseUserClientReportDaily.fields.user')}</Form.Label>
                    {isEditing ? (
                        <Form.Select
                            name="users"
                            style={{ width: "20%" }}
                            value={selectedUserId ?? ""}
                            onChange={(e) => setSelectedUserId(Number(e.target.value))}
                        >
                            {usuarios?.map((user) => (
                                <option key={user.id} value={String(user.id)}>
                                    {user.username}
                                </option>

                            ))}
                        </Form.Select>

                    ) : (
                        <Form.Control
                            type="text"
                            value={warehouseUserClientReportDaily.data[0].userName}
                            readOnly
                            style={{ cursor: "pointer", textDecoration: "underline", width: "20%", backgroundColor: "#f0f0f0" }}
                            onClick={() => handleNavigation(`/user/${warehouseUserClientReportDaily.data[0].userId}`)}
                        />
                    )}
                </Form.Group>


            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewWarehouseUserClientReportDailyById;
